import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config/apiClientConfig';
import {
	dependentSchema as createDependentSchema,
	requestSchema as createJourneyRequestSchema,
	principalDetailsSchema as createPrincipalDetailsSchema,
	memberIdSchema,
} from '../create-journey';

const membersCommonSchema = z.object({
	passportNo: z.string().optional(),
	emiratesIdNumber: z.string().optional(),
	visaFileNumber: z.string().optional(),
	emirateId: z.number().optional(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	unifiedNumber: z.string().optional(),
	occupationId: z.number().optional(),
});

const principalDetailsSchema = createPrincipalDetailsSchema
	.merge(membersCommonSchema)
	.merge(memberIdSchema)
	.merge(z.object({ relationshipTypeId: z.number() }));

const dependentDetailsSchema = createDependentSchema.merge(memberIdSchema).merge(membersCommonSchema);

const policyDetailSchema = z.object({
	id: z.number(),
	policyReferenceNo: z.string().optional(),
	policyNumber: z.string().optional(),
	policyIssueDate: z.string().optional(),
	effectiveDateFrom: z.string(),
	effectiveDateTo: z.string(),
	policyStatusId: z.number(),
});

const responseSchema = createJourneyRequestSchema.omit({ policyTypeId: true }).merge(
	z.object({
		encryptedJourneyId: z.string(),
		quoteStatusId: z.number(),
		isPaymentPending: z.boolean(),
		quoteReferenceNumber: z.string().nullable(),
		quoteValidity: z.string().nullable(),
		principalDetails: principalDetailsSchema,
		dependents: z.array(dependentDetailsSchema).optional(),
		policyDetail: policyDetailSchema.optional(),
		policyType: z.number(),
		journeyPageId: z.number(),
		memberCount: z.number(),
		emirateId: z.number(),
		channelTypeId: z.number(),
		quotePlanCategoryId: z.number().optional(),
		planCategoryIdForSelectedPlan: z.number().optional(),
		planId: z.number().optional(),
		maxValidityDateForEffectiveDate: z.string().optional(),
		effectiveDateFrom: z.string().optional(),
	})
);

const getJourney = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/Member/:journeyId',
		method: 'GET',
		requestParameters: { journeyId: ['required', 'string'] },
		responseSchema: responseSchema,
		errorCodes: ['InvalidJounreyId'],
	});

export default getJourney;

export type PrincipalDetails = z.infer<typeof principalDetailsSchema>;

export type DependentDetails = z.infer<typeof dependentDetailsSchema>;

export type GetJourneyDetails = TypeOfAPIResponse<ReturnType<typeof getJourney>>;

export type GetJourneyErrors = TypeOfAPIError<ReturnType<typeof getJourney>>;
